import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Box, Typography, CircularProgress } from "@mui/material";
import { getToken, hasValidToken } from "../helpers/tokenStorage";

/**
 * Cross-app SSO handoff endpoint.
 *
 * A peer c2a app (e.g. admin.clue2.app) redirects here with
 * ?returnTo=<absolute-url>. If we have a valid JWT in localStorage,
 * we bounce back to returnTo with #token=<jwt> in the URL fragment.
 * Otherwise we send the caller to /login; after that succeeds the
 * user hits this route again with the token in place.
 *
 * The JWT rides in the URL FRAGMENT, not the query string, so it
 * never appears in server access logs or the Referer header on the
 * final target's first request.
 *
 * Trust model: the JWT is HS256-signed by the coordinator and
 * verified independently by the destination app on every request.
 * A tampered token fails signature and returns 401 there — this
 * handoff is a transport, not a trust boundary.
 */

// Add hostnames here as we deploy more admin/tooling surfaces.
// Wildcards are NOT supported — every allowed host is explicit.
//
// Some entries are aspirational (future custom-domain assignment): the
// short `.clue2.app` domains are pre-registered in the allowlist so
// domain-assignment PRs don't also need a console PR. Aspirational
// entries are harmless when they're NXDOMAIN — a request for that
// hostname would never route to us. Once DNS lists them, they light up
// automatically. See SCK-453 for context.
const ALLOWED_RETURN_HOSTS = new Set([
  // admin dashboard
  "admin.clue2.app",                    // aspirational (future custom domain)
  "admin.control.apps.clue2.app",       // live (auto-assigned by coord)
  // int-tests-ui dashboard (SCK-447)
  "int-tests-ui.clue2.app",             // aspirational (future custom domain)
  "int-tests-ui.clues.apps.clue2.app",  // live (auto-assigned by coord)
  // local dev
  "localhost",
  "127.0.0.1",
]);

function isReturnToAllowed(rawUrl) {
  try {
    const parsed = new URL(rawUrl);
    if (parsed.protocol !== "https:" && parsed.hostname !== "localhost" && parsed.hostname !== "127.0.0.1") {
      return false;
    }
    return ALLOWED_RETURN_HOSTS.has(parsed.hostname);
  } catch {
    return false;
  }
}

export default function SsoHandoff() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  const returnTo = searchParams.get("returnTo") || "";

  useEffect(() => {
    if (!returnTo) {
      setError("Missing returnTo parameter.");
      return;
    }
    if (!isReturnToAllowed(returnTo)) {
      setError(`Return-to host is not allowed: ${(() => { try { return new URL(returnTo).hostname; } catch { return returnTo; } })()}`);
      return;
    }

    if (hasValidToken()) {
      const token = getToken();
      // Fragment, NOT query string — keeps the JWT out of access logs.
      const separator = returnTo.includes("#") ? "&" : "#";
      window.location.href = `${returnTo}${separator}token=${encodeURIComponent(token)}`;
      return;
    }

    // No valid token yet — send the user to /login. Preserve the
    // handoff URL as `from` so Login can bounce back here after
    // authenticating. Same pattern the existing app uses.
    navigate("/login", {
      replace: true,
      state: {
        from: {
          pathname: "/sso-handoff",
          search: `?returnTo=${encodeURIComponent(returnTo)}`,
        },
      },
    });
  }, [returnTo, navigate]);

  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: "center", color: "#ef4444" }}>
        <Typography variant="h6" sx={{ mb: 1 }}>SSO handoff failed</Typography>
        <Typography variant="body2">{error}</Typography>
        <Typography variant="caption" sx={{ display: "block", mt: 2, color: "#94a3b8" }}>
          Contact a Clue2App platform admin if you reached this page unexpectedly.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, textAlign: "center" }}>
      <CircularProgress size={32} sx={{ color: "#F97316" }} />
      <Typography variant="body2" sx={{ mt: 2, color: "#94a3b8" }}>
        Signing you in…
      </Typography>
    </Box>
  );
}

